'use client';

import { faArrowUp, faCircleNotch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import { useProjectResponseModeContext } from '@/shared-survey';
import type { GetProjectQuery } from '@/survey-graphql';

import { shouldRenderMobileFooter } from './survey-ai-forms-ai-section-mobile-footer.helpers';
import { useInputComposer } from './survey-ai-forms-form-section.hooks';
import SurveyAiFormsProgressDots from './survey-ai-forms-progress-dots';
import SurveyAiFormsVoiceInput from './survey-ai-forms-voice-input';
import type { FormAnswerValue, ResponseMode } from './survey-ai-forms.types';

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

interface Props {
  currentQuestion: ProjectQuestion | null;
  questions?: ProjectQuestion[];
  onResponseSubmit: (value: FormAnswerValue) => void;
  onNext?: () => void;
  onSubmit?: () => void;
  onQuestionFocus?: (questionId: string) => void;
  saveTranscript: (questionId: string, transcript: string) => void;
  responseMode: ResponseMode;
  isVoiceEnabled: boolean;
  isSupported?: boolean;
  currentQuestionIndex: number;
  totalQuestions: number;
  onFocus?: () => void;
  registerVoiceStopCallback?: (callback: (() => void) | null) => void;
  transcriptValue: string;
  setTranscriptValue: (value: string) => void;
  isFormattingTranscript: boolean;
  hasValue: boolean;
  canSubmit: boolean;
  isProcessingResponse?: boolean;
  isUserVoiceEnabled?: boolean;
}

export default function SurveyAiFormsAiSectionMobileFooter({
  currentQuestion,
  questions,
  onNext,
  onSubmit,
  onQuestionFocus,
  responseMode,
  isVoiceEnabled,
  currentQuestionIndex,
  totalQuestions,
  onFocus,
  registerVoiceStopCallback,
  transcriptValue,
  setTranscriptValue,
  isFormattingTranscript,
  canSubmit,
  isProcessingResponse = false,
  isUserVoiceEnabled = false,
}: Props) {
  const tChat = useTranslations('Chat');
  const tAiForms = useTranslations('AIForms.aria');

  const { isListening } = useProjectResponseModeContext();

  const isLoading = isFormattingTranscript || isProcessingResponse;

  const { showNextText } = useInputComposer({
    value: transcriptValue,
    onChange: setTranscriptValue,
    onSubmit: onNext,
    responseMode,
    instanceId: currentQuestion?.id || 'mobile-ai-section',
    isVoiceActive: isVoiceEnabled,
    isListening: isListening,
    questionType: currentQuestion?.type,
    loading: isFormattingTranscript,
  });

  const handleNextClick = showNextText ? onNext : onSubmit;

  if (!shouldRenderMobileFooter()) {
    return null;
  }

  return (
    <div
      data-test='mobile-footer'
      className='mt-4 flex items-center justify-between gap-3 p-4'
    >
      {isUserVoiceEnabled ? (
        <SurveyAiFormsVoiceInput
          value={transcriptValue}
          isActive={true}
          onTranscriptChange={setTranscriptValue}
          onFocus={onFocus}
          registerStopCallback={registerVoiceStopCallback}
          instanceId={currentQuestion?.id || 'mobile-ai-section'}
          hideLabel={true}
          className='size-12 rounded-full p-0'
          loading={isFormattingTranscript}
        />
      ) : (
        <div className='size-12'></div>
      )}

      <div className='flex items-end justify-center'>
        <SurveyAiFormsProgressDots
          currentIndex={currentQuestionIndex}
          total={totalQuestions}
          questions={questions}
          onQuestionClick={onQuestionFocus}
        />
      </div>

      <button
        type='button'
        onClick={handleNextClick}
        disabled={!canSubmit || isLoading}
        data-active={canSubmit && !isLoading}
        data-voice-active={showNextText && !isLoading}
        data-loading={isLoading}
        data-test='next-button'
        className='flex h-12 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full bg-(--brand-bg-unselected) [font-family:var(--brand-font)] text-sm/4 font-medium text-black disabled:cursor-not-allowed data-[active=true]:bg-(--brand-primary) data-[active=true]:shadow-[0px_4px_24px_0px_rgba(52,52,111,0.24)] data-[voice-active=false]:w-12 data-[voice-active=true]:px-4'
        aria-label={showNextText ? tChat('next') : tAiForms('sendResponse')}
      >
        {isLoading ? (
          <FontAwesomeIcon
            icon={faCircleNotch}
            className='size-5 animate-spin text-(--brand-primary)'
          />
        ) : (
          <>
            {showNextText && (
              <span className='text-(--brand-primary-contrast)'>
                {tChat('next')}
              </span>
            )}
            <FontAwesomeIcon
              icon={faArrowUp}
              className={cn(
                'size-3 rotate-90',
                (canSubmit && !isLoading) || showNextText
                  ? 'text-(--brand-primary-contrast)'
                  : 'text-(--brand-text-unselected)'
              )}
              data-test='next-arrow'
            />
          </>
        )}
      </button>
    </div>
  );
}
